import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  enhancedApi as providerDirectoryV2IdentityApi,
  type ProviderServiceSearchResultV2,
} from '@/services/api/generated/providerDirectoryV2IdentityService';

import { CSVImportSection } from '../CSVImportSection';
import { FilterChips, type FilterKey } from './components/FilterChips';
import { ProviderCard } from './components/ProviderCard';
import { SearchBar } from './components/SearchBar';

export const ProviderSearchHome = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';

  const [query, setQuery] = useState(initialQ);
  const debouncedQuery = useDebouncedValue(query, 250);

  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(
    new Set(),
  );

  // Sync query to URL
  useEffect(() => {
    if (debouncedQuery) {
      setSearchParams({ q: debouncedQuery }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [debouncedQuery, setSearchParams]);

  // Resolve search using RTK Query directly to preserve full response shape
  const shouldSearch = debouncedQuery.trim().length >= 2;
  const { data: resolveData, isFetching: isSearching } =
    providerDirectoryV2IdentityApi.endpoints.providerDirectoryV2Resolve.useQuery(
      { q: debouncedQuery.trim(), limit: 15 },
      { skip: !shouldSearch },
    );

  // Flatten into recommended + alternates with full data
  const { recommended, alternates } = useMemo(() => {
    if (!resolveData)
      return {
        recommended: null as ProviderServiceSearchResultV2 | null,
        alternates: [] as ProviderServiceSearchResultV2[],
      };
    return {
      recommended: resolveData.recommended ?? null,
      alternates: resolveData.alternates ?? [],
    };
  }, [resolveData]);

  // Apply client-side filters
  const filterResult = useCallback(
    (result: ProviderServiceSearchResultV2): boolean => {
      if (activeFilters.size === 0) return true;
      if (
        activeFilters.has('portal_eligible') &&
        !result.provider_service.portal_eligible
      )
        return false;
      if (
        activeFilters.has('has_request_plan') &&
        !result.clinical_location?.has_active_request_plan
      )
        return false;
      if (activeFilters.has('verified_contacts')) {
        if (
          !result.verified_contact_count &&
          result.records_destination_status !== 'VERIFIED' &&
          result.bills_destination_status !== 'VERIFIED'
        )
          return false;
      }
      if (activeFilters.has('has_fax')) {
        if (!result.best_contacts?.fax?.value) return false;
      }
      if (activeFilters.has('specialty')) {
        if (
          !result.provider_service.service_type ||
          result.provider_service.service_type === 'GENERAL'
        )
          return false;
      }
      return true;
    },
    [activeFilters],
  );

  const filteredRecommended =
    recommended && filterResult(recommended) ? recommended : null;
  const filteredAlternates = alternates.filter(filterResult);

  const totalResults =
    (filteredRecommended ? 1 : 0) + filteredAlternates.length;
  const hasSearched = shouldSearch && !isSearching;

  const handleToggleFilter = useCallback((key: FilterKey) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleClear = useCallback(() => {
    setQuery('');
    setActiveFilters(new Set());
  }, []);

  // Build results summary text
  const resultsSummary = () => {
    if (!hasSearched) return null;
    if (totalResults === 0) return 'No results found';
    const parts: string[] = [];
    if (filteredRecommended) parts.push('1 recommended');
    if (filteredAlternates.length > 0) {
      parts.push(
        `${filteredAlternates.length} alternate${filteredAlternates.length !== 1 ? 's' : ''}`,
      );
    }
    return parts.join(' + ');
  };

  return (
    <div className="flex flex-col items-center">
      {/* Hero section */}
      <div className="w-full max-w-2xl pt-12 pb-8 text-center">
        <h1 className="font-serif text-4xl font-bold text-info-dark tracking-tight">
          Provider Directory
        </h1>
        <p className="mt-2 text-sm font-medium text-info-light">
          Search verified providers across all locations and specialties
        </p>

        {/* Action buttons */}
        <div className="mt-4 flex items-center justify-center">
          <CSVImportSection />
        </div>
      </div>

      {/* Search bar */}
      <div className="w-full max-w-2xl">
        <SearchBar
          query={query}
          onQueryChange={setQuery}
          onClear={handleClear}
          isSearching={isSearching}
          autoFocus
        />
      </div>

      {/* Filters + results */}
      {shouldSearch && (
        <div className="mt-6 w-full max-w-2xl">
          {/* Filter chips */}
          <FilterChips
            activeFilters={activeFilters}
            onToggle={handleToggleFilter}
            className="mb-4"
          />

          {/* Results header */}
          <div className="mb-3 flex items-center justify-between">
            {hasSearched ? (
              <p className="text-sm font-medium text-info-light">
                {resultsSummary()}
              </p>
            ) : (
              <p className="text-sm font-medium text-info-light">
                Searching…
              </p>
            )}
            <div className="flex items-center gap-1 text-sm text-info-light">
              <span>Sort by</span>
              <span className="font-medium text-info-dark">Relevance</span>
              <ChevronDown size={14} />
            </div>
          </div>

          {/* Result cards */}
          <div className="flex flex-col gap-3">
            {filteredRecommended && (
              <ProviderCard result={filteredRecommended} />
            )}
            {filteredAlternates.map((result) => (
              <ProviderCard
                key={String(result.provider_service.id)}
                result={result}
              />
            ))}
          </div>

          {/* Empty state */}
          {hasSearched && totalResults === 0 && (
            <div className="mt-8 flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-background-gray p-8 text-center">
              <p className="text-sm font-medium text-info-light">
                No providers found matching your search
              </p>
              <p className="text-xs font-medium text-zinc-500">
                Try adjusting your search terms or filters
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty state when no query */}
      {!shouldSearch && !query && (
        <div className="mt-12 flex flex-col items-center gap-2 text-center">
          <div className="rounded-full bg-primary-light p-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </div>
          <p className="mt-2 text-sm font-medium text-info-light">
            Start typing to search the provider directory
          </p>
        </div>
      )}
    </div>
  );
};
